/**
 * SuggestSimilarTransfersModal Component
 * Modal for viewing and batch-marking similar transfers
 * WP-TRANSFER-1: Transfer Detection and Management
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Spinner, Alert } from '@/components/ui';
import { formatCurrency, formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';
import {
  transferService,
  type SimilarTransferSuggestion,
  type BatchMarkTransfersResponse,
} from '@/services/transactions';
import type { TransactionWithSplits } from '@/services/transactions';
import { ApiClientError } from '@/services/api';

// ============================================================================
// Types
// ============================================================================

export interface SuggestSimilarTransfersModalProps {
  transaction: TransactionWithSplits;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (response: BatchMarkTransfersResponse) => void;
}

// ============================================================================
// Component
// ============================================================================

export default function SuggestSimilarTransfersModal({
  transaction,
  isOpen,
  onClose,
  onSuccess,
}: SuggestSimilarTransfersModalProps) {
  // State
  const [suggestions, setSuggestions] = useState<SimilarTransferSuggestion[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state and fetch suggestions when modal opens
  useEffect(() => {
    if (!isOpen) {
      setSuggestions([]);
      setSelectedIds(new Set());
      setError(null);
      return;
    }

    const fetchSuggestions = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await transferService.suggestSimilarTransfers(transaction.id);
        setSuggestions(result.suggestions);
        // Pre-select all suggestions with high confidence
        const preselected = new Set<string>();
        result.suggestions.forEach((s) => {
          if (s.confidence >= 0.7 && s.suggestedPair) {
            preselected.add(s.transaction.id);
          }
        });
        setSelectedIds(preselected);
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.detail);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to load similar transactions');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestions();
  }, [isOpen, transaction.id]);

  // Handle select/deselect
  const handleToggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === suggestions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(suggestions.map((s) => s.transaction.id)));
    }
  }, [selectedIds.size, suggestions]);

  // Build batch request from selections
  const buildBatchRequest = useCallback(() => {
    return suggestions
      .filter((s) => selectedIds.has(s.transaction.id))
      .map((s) => ({
        transactionId: s.transaction.id,
        targetAccountId: s.suggestedPair?.accountId ?? '',
        pairedTransactionId: s.suggestedPair?.id,
      }))
      .filter((item) => item.targetAccountId); // Only include items with a target account
  }, [suggestions, selectedIds]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    const transfers = buildBatchRequest();
    if (transfers.length === 0) {
      setError('No valid transfers selected. Each transfer needs a target account.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await transferService.batchMarkTransfers(transfers);
      onSuccess(response);
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to mark transfers');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [buildBatchRequest, onSuccess, onClose]);

  // Count how many selected have valid pairs
  const validSelectionCount = useMemo(() => {
    return buildBatchRequest().length;
  }, [buildBatchRequest]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="similar-transfers-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2
            id="similar-transfers-modal-title"
            className="text-lg font-semibold text-gray-900"
          >
            Similar Transfers Found
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-4">
          We found transactions similar to the one you just marked as a transfer.
          Would you like to mark them as transfers too?
        </p>

        {/* Error message */}
        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="mt-4 text-sm text-gray-500">Finding similar transactions...</p>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="py-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
            <p className="mt-4 text-gray-500">No similar transfers found.</p>
          </div>
        ) : (
          <>
            {/* Select all header */}
            <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-t-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.size === suggestions.length}
                  onChange={handleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  {selectedIds.size === suggestions.length ? 'Deselect All' : 'Select All'}
                </span>
                <span className="text-sm text-gray-500">
                  ({suggestions.length} found)
                </span>
              </label>
            </div>

            {/* Suggestions list */}
            <div className="border border-t-0 border-gray-200 rounded-b-lg overflow-hidden mb-6">
              <div className="max-h-96 overflow-y-auto">
                {suggestions.map((suggestion) => (
                  <SuggestionRow
                    key={suggestion.transaction.id}
                    suggestion={suggestion}
                    isSelected={selectedIds.has(suggestion.transaction.id)}
                    onToggle={() => handleToggle(suggestion.transaction.id)}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {/* Footer buttons */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Skip
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={validSelectionCount === 0 || isSubmitting || isLoading}
            isLoading={isSubmitting}
          >
            Mark {validSelectionCount} as Transfers
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface SuggestionRowProps {
  suggestion: SimilarTransferSuggestion;
  isSelected: boolean;
  onToggle: () => void;
}

function SuggestionRow({ suggestion, isSelected, onToggle }: SuggestionRowProps) {
  const { transaction, suggestedPair, confidence } = suggestion;
  const displayName = transaction.merchantName || transaction.name || 'Unknown';
  const isNegative = transaction.amount < 0;

  // Confidence badge color
  const confidencePercent = Math.round(confidence * 100);
  const confidenceColor =
    confidencePercent >= 80
      ? 'text-green-600 bg-green-50'
      : confidencePercent >= 50
      ? 'text-yellow-600 bg-yellow-50'
      : 'text-gray-600 bg-gray-50';

  return (
    <div
      className={cn(
        'flex items-start px-4 py-3 border-b border-gray-100 last:border-b-0',
        isSelected && 'bg-primary-50'
      )}
    >
      <label className="flex items-start w-full gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <div className="flex-1 min-w-0">
          {/* Source transaction */}
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {displayName}
              </p>
              <p className="text-xs text-gray-500">
                {formatDate(transaction.date)}
              </p>
            </div>
            <span
              className={cn(
                'text-sm font-medium whitespace-nowrap ml-2',
                isNegative ? 'text-gray-900' : 'text-green-600'
              )}
            >
              {isNegative ? '-' : '+'}
              {formatCurrency(Math.abs(transaction.amount), transaction.currency)}
            </span>
          </div>

          {/* Arrow and suggested pair */}
          {suggestedPair && (
            <div className="mt-2 pl-4 border-l-2 border-gray-200">
              <div className="flex items-center text-xs text-gray-500 mb-1">
                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
                Suggested pair
              </div>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 truncate">
                    {suggestedPair.merchantName || suggestedPair.name || 'Unknown'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDate(suggestedPair.date)}
                  </p>
                </div>
                <span
                  className={cn(
                    'text-sm font-medium whitespace-nowrap ml-2',
                    suggestedPair.amount < 0 ? 'text-gray-700' : 'text-green-600'
                  )}
                >
                  {suggestedPair.amount < 0 ? '-' : '+'}
                  {formatCurrency(Math.abs(suggestedPair.amount), suggestedPair.currency)}
                </span>
              </div>
            </div>
          )}

          {/* No pair warning */}
          {!suggestedPair && (
            <p className="mt-1 text-xs text-yellow-600">
              No matching transaction found - cannot be marked automatically
            </p>
          )}
        </div>

        {/* Confidence badge */}
        <span className={cn('text-xs px-1.5 py-0.5 rounded whitespace-nowrap', confidenceColor)}>
          {confidencePercent}%
        </span>
      </label>
    </div>
  );
}
